// src/hooks/useInventoryLive.js
// --- UPDATE (Inventory MOW+ v2) — NEW FILE -----------------------------------------------
// One place for "this screen must follow live inventory changes":
//   useInventoryLive(onChange, { events, debounceMs, pollMs, filter, enabled })
// • subscribes to the FlowSense WebSocket events (inventory_update — item create / edit /
//   delete, stock movements, links, vehicles, categories, alert dismissals; low_stock_alert;
//   work_order_completed) and calls onChange once per burst (debounced);
// • `filter(msg)` lets a screen ignore events that are not about it (e.g. one item's ledger);
// • polling fallback while the tab is visible, in case the socket is down;
// • returns { connected, lastEvent, lastEventAt, events, updatedAt, markUpdated } for the
//   live indicator. `connected` is null when the socket service does not report its state.
// Listeners are always removed on unmount.
// --- END UPDATE ---------------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';

export const LIVE_INVENTORY_EVENTS = Object.freeze(['inventory_update', 'low_stock_alert', 'work_order_completed']);

/** Best effort: different builds of websocketService expose their state differently. */
export function socketConnected() {
    try {
        const s = webSocketService;
        if (!s) return null;
        if (typeof s.isConnected === 'function') return !!s.isConnected();
        if (typeof s.isConnected === 'boolean') return s.isConnected;
        if (typeof s.connected === 'boolean') return s.connected;
        const sock = s.socket || s.ws;
        if (sock && typeof sock.readyState === 'number') return sock.readyState === 1;
        if (sock && typeof sock.connected === 'boolean') return sock.connected;
    } catch (_) { /* unknown */ }
    return null;
}

const payloadOf = (msg) => (msg && (msg.payload || msg.data)) || msg || {};

export default function useInventoryLive(onChange, {
    events = LIVE_INVENTORY_EVENTS, debounceMs = 900, pollMs = 120000, filter = null, enabled = true,
} = {}) {
    const [lastEvent, setLastEvent] = useState(null);
    const [count, setCount] = useState(0);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [connected, setConnected] = useState(() => socketConnected());
    const cb = useRef(onChange); cb.current = onChange;
    const flt = useRef(filter); flt.current = filter;
    const timer = useRef(null);
    const mounted = useRef(true);

    const markUpdated = useCallback(() => { if (mounted.current) setUpdatedAt(new Date()); }, []);

    useEffect(() => {
        mounted.current = true;
        if (!enabled) return () => { mounted.current = false; };
        const key = events.join('|');
        const list = key ? key.split('|') : [];
        const handlers = list.map((type) => {
            const h = (msg) => {
                if (!mounted.current) return;
                const p = payloadOf(msg);
                if (flt.current && !flt.current({ type, ...p })) return;
                setLastEvent({ type, reason: p.reason || null, item_id: p.item_id ?? null, at: new Date() });
                setCount((c) => c + 1);
                setConnected(true);
                clearTimeout(timer.current);
                timer.current = setTimeout(() => { if (mounted.current) cb.current?.({ type, ...p }); }, debounceMs);
            };
            try { webSocketService.subscribe(type, h); } catch (_) { /* socket not ready — polling covers it */ }
            return [type, h];
        });
        const poll = pollMs > 0 ? setInterval(() => {
            setConnected(socketConnected());
            if (typeof document === 'undefined' || !document.hidden) cb.current?.({ type: 'poll' });
        }, pollMs) : null;
        return () => {
            mounted.current = false;
            clearTimeout(timer.current);
            if (poll) clearInterval(poll);
            handlers.forEach(([type, h]) => { try { webSocketService.unsubscribe(type, h); } catch (_) { /* */ } });
        };
    }, [enabled, events.join('|'), debounceMs, pollMs]); // eslint-disable-line react-hooks/exhaustive-deps

    return { connected, lastEvent, lastEventAt: lastEvent?.at || null, events: count, updatedAt, markUpdated };
}
